import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiDownload, FiMail, FiZap, FiGlobe, FiShield, FiSliders, FiMessageSquare, FiEdit3, FiCheckCircle, FiArrowRight, FiChrome } from 'react-icons/fi';
import Footer from '../components/Layout/Footer';

const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/replycraft-ai-email-reply/aomdlgdljkpfoambklbjonagckenjcpm';

const FEATURES = [
  { icon: <FiMessageSquare size={24} />, title: 'One-Click Replies', desc: 'Click ReplyCraft inside any compose window. Generate a reply in seconds without leaving your inbox.' },
  { icon: <FiSliders size={24} />, title: '5 Tones & 3 Lengths', desc: 'Professional, Warm, Assertive, Casual, or Diplomatic. Short, Medium, or Long. Mix and match to fit every situation.' },
  { icon: <FiGlobe size={24} />, title: '23 Languages', desc: 'Reply in English, Spanish, French, German, Hindi, Japanese, Korean, Arabic, and 15 more languages.' },
  { icon: <FiEdit3 size={24} />, title: 'Reply Instructions', desc: 'Tell the AI what to say: "Accept but suggest Thursday" — it crafts the perfect reply around your intent.' },
  { icon: <FiZap size={24} />, title: 'Fix My Draft', desc: 'Write a rough draft, click Fix — get a polished, professional version back instantly.' },
  { icon: <FiShield size={24} />, title: 'Private & Secure', desc: 'Your emails are never stored or used for training. Everything is encrypted and processed in real-time.' }
];

const PLATFORMS = [
  { name: 'Gmail', status: 'Fully Supported', color: '#22c55e' },
  { name: 'Outlook Web', status: 'Supported', color: '#22c55e' },
  { name: 'Yahoo Mail', status: 'Supported', color: '#22c55e' },
  { name: 'AOL Mail', status: 'Supported', color: '#22c55e' }
];

const STEPS = [
  { num: '1', title: 'Install the Extension', desc: 'Click "Add to Chrome" — takes 2 seconds.' },
  { num: '2', title: 'Sign In', desc: 'Click the ReplyCraft icon in your toolbar and log in with your account.' },
  { num: '3', title: 'Open Gmail & Reply', desc: 'Open any email, click Reply, and you\'ll see the ReplyCraft button next to Send.' },
  { num: '4', title: 'Generate & Insert', desc: 'Pick your tone, length, and language. Click Generate. Click Insert. Done.' }
];

export default function Extension() {
  const navigate = useNavigate();

  return (
    <div style={styles.page}>
      <nav style={styles.nav}>
        <Link to="/" style={styles.brand}>ReplyCraft</Link>
        <button onClick={() => navigate('/')} style={styles.tryBtn}>Open App</button>
      </nav>

      <div style={styles.container}>
        {/* Hero */}
        <div style={styles.hero}>
          <div style={styles.heroBadge}>
            <FiChrome size={16} /> Chrome Extension
          </div>
          <h1 style={styles.heroTitle}>AI Replies, Right Inside Your Inbox</h1>
          <p style={styles.heroSubtitle}>
            Generate professional email replies in seconds — without leaving Gmail, Outlook, or Yahoo Mail.
            One click to generate. One click to insert. That's it.
          </p>
          <a href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer" style={styles.installBtn}>
            <FiDownload size={20} />
            Add to Chrome — It's Free
          </a>
          <p style={styles.heroNote}>Free credits included. No credit card required.</p>
        </div>

        {/* How It Works */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>How It Works</h2>
          <div style={styles.stepsGrid}>
            {STEPS.map((step, i) => (
              <div key={i} style={styles.stepCard}>
                <div style={styles.stepNum}>{step.num}</div>
                <h3 style={styles.stepTitle}>{step.title}</h3>
                <p style={styles.stepDesc}>{step.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Features */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Everything You Need</h2>
          <div style={styles.featuresGrid}>
            {FEATURES.map((f, i) => (
              <div key={i} style={styles.featureCard}>
                <div style={styles.featureIcon}>{f.icon}</div>
                <h3 style={styles.featureTitle}>{f.title}</h3>
                <p style={styles.featureDesc}>{f.desc}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Supported Platforms */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Works With Your Email</h2>
          <div style={styles.platformsGrid}>
            {PLATFORMS.map((p, i) => (
              <div key={i} style={styles.platformCard}>
                <FiMail size={28} style={{ color: 'var(--accent)', marginBottom: 8 }} />
                <h3 style={styles.platformName}>{p.name}</h3>
                <span style={{ ...styles.platformStatus, color: p.color }}>
                  <FiCheckCircle size={14} /> {p.status}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* Web App vs Extension */}
        <div style={styles.section}>
          <h2 style={styles.sectionTitle}>Extension vs Web App</h2>
          <div style={styles.compareGrid}>
            <div style={styles.compareCard}>
              <h3 style={{ ...styles.compareTitle, color: 'var(--accent)' }}>Chrome Extension</h3>
              <ul style={styles.compareList}>
                <li>Replies generated inside Gmail/Outlook</li>
                <li>One-click insert into compose</li>
                <li>No copy-pasting between tabs</li>
                <li>Reads email context automatically</li>
                <li>Best for quick daily replies</li>
              </ul>
            </div>
            <div style={styles.compareCard}>
              <h3 style={{ ...styles.compareTitle, color: 'var(--text-secondary)' }}>Web App</h3>
              <ul style={styles.compareList}>
                <li>Paste any message from any platform</li>
                <li>Save & manage favorite replies</li>
                <li>Full dashboard & settings</li>
                <li>Works on any browser or device</li>
                <li>Best for longer, detailed replies</li>
              </ul>
            </div>
          </div>
          <p style={styles.compareTip}>Use both — same account, same credits, same AI.</p>
        </div>

        {/* CTA */}
        <div style={styles.cta}>
          <h2 style={{ fontSize: 28, fontWeight: 800, marginBottom: 8 }}>Reply Smarter, Starting Now</h2>
          <p style={{ color: 'var(--text-secondary)', marginBottom: 24, fontSize: 16 }}>
            Join thousands of professionals saving hours on email every week.
          </p>
          <a href={CHROME_STORE_URL} target="_blank" rel="noopener noreferrer" style={styles.installBtn}>
            <FiDownload size={20} />
            Add to Chrome — It's Free
          </a>
          <div style={styles.ctaLinks}>
            <Link to="/signup" style={styles.ctaLink}>Create free account</Link>
            <span style={{ color: 'var(--text-muted)' }}>|</span>
            <Link to="/how-it-works" style={styles.ctaLink}>How it works</Link>
            <span style={{ color: 'var(--text-muted)' }}>|</span>
            <Link to="/pricing" style={styles.ctaLink}>Pricing</Link>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex', flexDirection: 'column' },
  nav: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--border)' },
  brand: { fontSize: 20, fontWeight: 700, color: 'var(--accent)', textDecoration: 'none' },
  tryBtn: { padding: '8px 20px', background: 'var(--accent)', color: 'white', border: 'none', borderRadius: 8, fontSize: 14, fontWeight: 600, cursor: 'pointer' },
  container: { maxWidth: 860, margin: '0 auto', padding: '0 20px 60px', flex: 1 },

  // Hero
  hero: { textAlign: 'center', padding: '56px 0 48px' },
  heroBadge: { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '6px 16px', background: 'var(--accent-dim)', color: 'var(--accent)', borderRadius: 20, fontSize: 13, fontWeight: 600, marginBottom: 20 },
  heroTitle: { fontSize: 'clamp(28px, 6vw, 44px)', fontWeight: 800, lineHeight: 1.15, marginBottom: 16, color: 'var(--text-primary)' },
  heroSubtitle: { fontSize: 17, color: 'var(--text-secondary)', lineHeight: 1.6, maxWidth: 600, margin: '0 auto 28px' },
  installBtn: { display: 'inline-flex', alignItems: 'center', gap: 10, padding: '14px 36px', background: 'linear-gradient(135deg, #6366f1, #4f46e5)', color: 'white', border: 'none', borderRadius: 12, fontSize: 17, fontWeight: 700, cursor: 'pointer', textDecoration: 'none', boxShadow: '0 4px 20px rgba(99, 102, 241, 0.35)' },
  heroNote: { fontSize: 13, color: 'var(--text-muted)', marginTop: 12 },

  // Sections
  section: { marginTop: 56 },
  sectionTitle: { fontSize: 28, fontWeight: 800, textAlign: 'center', marginBottom: 32, color: 'var(--text-primary)' },

  // Steps
  stepsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16 },
  stepCard: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 14, padding: 24, textAlign: 'center', position: 'relative' },
  stepNum: { width: 32, height: 32, borderRadius: '50%', background: 'var(--accent)', color: 'white', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, marginBottom: 12 },
  stepTitle: { fontSize: 15, fontWeight: 700, marginBottom: 6, color: 'var(--text-primary)' },
  stepDesc: { fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.5 },

  // Features
  featuresGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 16 },
  featureCard: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 14, padding: 24 },
  featureIcon: { color: 'var(--accent)', marginBottom: 10 },
  featureTitle: { fontSize: 16, fontWeight: 700, marginBottom: 6, color: 'var(--text-primary)' },
  featureDesc: { fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 },

  // Platforms
  platformsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 12 },
  platformCard: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 14, padding: 20, textAlign: 'center' },
  platformName: { fontSize: 16, fontWeight: 700, marginBottom: 4, color: 'var(--text-primary)' },
  platformStatus: { display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 12, fontWeight: 600 },

  // Compare
  compareGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 16 },
  compareCard: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 14, padding: 24 },
  compareTitle: { fontSize: 18, fontWeight: 700, marginBottom: 12 },
  compareList: { listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 8, fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 },
  compareTip: { textAlign: 'center', marginTop: 16, fontSize: 14, color: 'var(--text-muted)', fontStyle: 'italic' },

  // CTA
  cta: { textAlign: 'center', marginTop: 56, padding: 48, background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 16 },
  ctaLinks: { display: 'flex', justifyContent: 'center', gap: 12, marginTop: 20, fontSize: 13 },
  ctaLink: { color: 'var(--accent)', textDecoration: 'none', fontWeight: 500 }
};
